import { Injectable } from '@angular/core';
import { Observable, Subscriber, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { Coords } from 'src/app/data/utils/geolocation.utils';
import { HttpHeadersBuilder } from '../apis/utils/http-header-builder';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CmvHttpEntidadResponse } from 'src/app/models/apis/entities';
import { environment } from 'src/environments/environment';
import { LocationOperationResponse } from 'src/app/models/apis/location.model';
import { Store } from '@ngrx/store';
import {
	DEFAULT_PRODUCT_CODE,
	getProductIdByCode,
} from 'src/app/models/utils/mappers/products.mapper';
import { selectProductInfo } from 'src/app/state/selectors/pre-application.selectors';
import { DevicePermissionsService } from './device-permissions.service';

@Injectable({
	providedIn: 'root',
})
export class GeolocationService {
	private readonly baseUrl: string = `${environment.urlBase}/onboarding/`;
	constructor(
		private httpClient: HttpClient,
		private store: Store,
		private devicePermissionsService: DevicePermissionsService
	) {}

	getCoords(): Observable<Coords> {
		return new Observable(observable => {
			this.devicePermissionsService.getPermission('camera').subscribe({
				next: permissionState => {
					if (permissionState === 'granted' || permissionState === 'prompt') {
						this.getCoordAndNotify(observable);
					}
					if (permissionState === 'denied') {
						observable.error(new Error('Permiso de la camara denegada'));
						observable.complete();
					}
				},
				error: (error: Error) => {
					observable.error(error);
					observable.complete();
				},
			});
		});
	}

	private getCoordAndNotify(observable: Subscriber<Coords>) {
		navigator.geolocation.getCurrentPosition(
			(successCurrentPosition: GeolocationPosition) => {
				try {
					const { latitude, longitude } = successCurrentPosition.coords;
					observable.next({
						latitude,
						longitude,
					});
					observable.complete();
				} catch (error) {
					console.log(
						'🚀 ~ file: geolocation.service.ts:19 ~ GeolocationService ~ getCoords ~ error:',
						error
					);
					observable.error(error);
					observable.complete();
				}
			},
			(error: any) => {
				observable.error(error);
				observable.complete();
			}
		);
	}

	/**
	 * Producto del proceso en curso. Se lee del store y no de CreditProductService
	 * porque ese depende de OnboardingService, que a su vez inyecta este servicio.
	 */
	private get currentProductId(): number {
		let productId = 0;
		this.store
			.select(selectProductInfo)
			.pipe(take(1))
			.subscribe(productInfo => {
				productId = productInfo?.productId ?? 0;
			});
		return productId > 0
			? productId
			: getProductIdByCode(DEFAULT_PRODUCT_CODE);
	}

	getLocationOperation(coords: Coords): Observable<LocationOperationResponse> {
		const headers = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.getHeaders();
		const params = new HttpParams()
			.set('id_producto', this.currentProductId)
			.set('latitud', coords.latitude)
			.set('longitud', coords.longitude);

		return this.httpClient
			.post<CmvHttpEntidadResponse<LocationOperationResponse>>(
				`${this.baseUrl}getLocationOperation`,
				null,
				{ headers, params }
			)
			.pipe(
				map((response: CmvHttpEntidadResponse<LocationOperationResponse>) => {
					if (response.Estado && response.Codigo === '200') {
						return response.Entidad;
					}
					throw new Error(response.Mensaje);
				})
			);
	}
}
